import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthProvider';
import { useTheme } from 'context/ThemeProvider';
import {
  LayoutDashboard,
  Gamepad,
  Building2,
  Gamepad2,
  Table2,
  Settings2,
  Users,
  Receipt,
  Swords,
  Lock,
  ShieldCheck,
  LogOut,
  Menu,
  Sun,
  Moon,
  Spade,
  ChevronDown,
  ScrollText,
  type LucideIcon,
} from 'lucide-react';

interface NavChild {
  name: string;
  path: string;
  icon: LucideIcon;
}

interface NavItem {
  name: string;
  path?: string;
  icon: LucideIcon;
  permission?: string;
  superAdminOnly?: boolean;
  children?: NavChild[];
}

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout, hasPermission } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const navItems: NavItem[] = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, permission: 'dashboard' },
    { name: 'Games', path: '/games', icon: Gamepad, permission: 'games' },
    { name: 'Consumers', path: '/consumers', icon: Building2, permission: 'consumers' },
    { name: 'Variants', path: '/variants', icon: Gamepad2, permission: 'variants' },
    { name: 'Tables', path: '/tables', icon: Table2, permission: 'tables' },
    { name: 'Players', path: '/players', icon: Users, permission: 'players' },
    { name: 'Game Config', path: '/config', icon: Settings2, permission: 'config' },
    { name: 'Battles', path: '/battles', icon: Swords, permission: 'battles' },
    { name: 'Transactions', path: '/wallet', icon: Receipt, permission: 'wallet' },
    { name: 'Private Tables', path: '/private-tables', icon: Lock, permission: 'private-tables' },
    { name: 'Roles', path: '/roles', icon: ShieldCheck, superAdminOnly: true },
    { name: 'Integration Logs', path: '/integration-logs', icon: ScrollText, superAdminOnly: true },
  ].filter((item) => (item.superAdminOnly ? !!admin?.isSuperAdmin : hasPermission(item.permission!)));

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initial = (admin?.username || 'A').charAt(0).toUpperCase();

  const pageTitle = (() => {
    for (const item of navItems) {
      if (item.children) {
        const child = item.children.find((c) => location.pathname.startsWith(c.path));
        if (child) return child.name;
      } else if (item.path && location.pathname.startsWith(item.path)) {
        return item.name;
      }
    }
    return 'Dashboard';
  })();

  const renderNav = (onNavigate?: () => void) => (
    <nav className="flex-1 px-4 py-5 overflow-y-auto">
      <p className="px-3 mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-faint">Menu</p>
      <div className="space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;

          if (item.children) {
            const groupActive = item.children.some((c) => location.pathname.startsWith(c.path));
            const isOpen = expandedGroups[item.name] ?? groupActive;
            return (
              <div key={item.name}>
                <button
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [item.name]: !isOpen }))}
                  className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                    groupActive ? 'text-heading' : 'text-muted hover:bg-subtle hover:text-heading'
                  }`}
                >
                  <Icon size={19} className={groupActive ? 'text-brand-gold' : 'text-faint group-hover:text-heading'} />
                  <span className="flex-1 text-left">{item.name}</span>
                  <ChevronDown size={16} className={`text-faint transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="mt-1 ml-4 pl-4 border-l border-line space-y-1 animate-fadeIn">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname.startsWith(child.path);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={onNavigate}
                          className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                            childActive
                              ? 'bg-brand-gold/15 text-brand-dark dark:text-brand-gold'
                              : 'text-muted hover:bg-subtle hover:text-heading'
                          }`}
                        >
                          <ChildIcon size={16} className={childActive ? 'text-brand-gold' : 'text-faint group-hover:text-heading'} />
                          <span>{child.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const isActive = !!item.path && location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path!}
              onClick={onNavigate}
              className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                isActive
                  ? 'bg-brand-gold/15 text-brand-dark dark:text-brand-gold'
                  : 'text-muted hover:bg-subtle hover:text-heading'
              }`}
            >
              {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-brand-gold" />}
              <Icon size={19} className={isActive ? 'text-brand-gold' : 'text-faint group-hover:text-heading'} />
              <span>{item.name}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );

  const sidebar = (
    <div className="flex h-full flex-col bg-card border-r border-line">
      {renderNav(() => setMobileOpen(false))}
      <div className="p-4 border-t border-line">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-muted hover:bg-brand-danger/10 hover:text-brand-danger transition-colors"
        >
          <LogOut size={19} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-dvh min-h-dvh w-full flex-col overflow-hidden bg-app text-body">
      {/* Single full-width top bar — flush to viewport top (no white gap) */}
      <header className="h-16 shrink-0 bg-card border-b border-line flex items-center z-20">
        <div className="hidden lg:flex items-center gap-3 h-full w-72 shrink-0 px-6 border-r border-line">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-brand-dark text-brand-gold shadow-sm">
            <Spade size={18} fill="currentColor" />
          </div>
          <div className="leading-tight min-w-0">
            <h1 className="text-base font-extrabold text-heading tracking-tight truncate">Teen Patti</h1>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-gold">Admin Console</p>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-between gap-3 min-w-0 px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 rounded-lg text-muted hover:bg-subtle hover:text-heading transition-colors"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <div className="leading-tight min-w-0">
              <h2 className="text-lg font-bold text-heading truncate">{pageTitle}</h2>
              <p className="hidden sm:block text-xs text-faint truncate">Home &nbsp;/&nbsp; {pageTitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button
              onClick={toggle}
              className="p-2.5 rounded-xl text-muted hover:bg-subtle hover:text-heading border border-line transition-colors"
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <div className="w-px h-8 bg-line mx-1 hidden sm:block" />
            <div className="flex items-center gap-3">
              <div className="text-right leading-tight hidden sm:block">
                <p className="text-sm font-bold text-heading">{admin?.username || 'Admin User'}</p>
                <p className="text-xs text-brand-gold font-semibold">{admin?.roleName || 'Administrator'}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-gold to-gold-500 flex items-center justify-center text-brand-dark font-bold shadow-sm">
                {initial}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <aside className="hidden lg:block w-72 shrink-0 z-10">{sidebar}</aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/60 animate-fadeIn" onClick={() => setMobileOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-72 pt-16 animate-scaleIn origin-left bg-card border-r border-line flex flex-col">
              {renderNav(() => setMobileOpen(false))}
              <div className="p-4 border-t border-line">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-muted hover:bg-brand-danger/10 hover:text-brand-danger transition-colors"
                >
                  <LogOut size={19} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 overflow-auto p-4 sm:p-6 lg:p-8 bg-app">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
